import {delayTimeout} from '@shared/helpers/function'
import {UserAgent} from '@shared/helpers/userAgent'

import {NAVER_CAFE_CLOSE} from '@web/core/urls'

const {isSafari} = new UserAgent(window.navigator.userAgent)

/**
 * 전달된 윈도우 객체의 팝업여부
 */
export const isPopupWindow = (target: Window = window) => !!target.opener && target.opener !== target

/**
 * 현재 브라우저 컨텍스트로 진입하기위한 navigaiton 타입이 back / forward인지 판명
 * bfcache에서 로드되었다면 true
 */
export const isBackForwarded = () => {
    const navigationEntry = window.performance.getEntriesByType('navigation')[0] as PerformanceEntry & {type: string}

    return (
        // safari 8, edge 12 이하 브라우저 대응. PerformanceNavigation 체크.
        performance.navigation?.type === PerformanceNavigation.TYPE_BACK_FORWARD ||
        // 상위 브라우저 대응. PerformanceNavigaionTiming 체크
        navigationEntry?.type === 'back_forward'
    )
}

/**
 * 이전 페이지로 돌아가기
 * @param history
 */
export const goBack = ({history = window.history, steps}: {history?: History; steps?: number} = {}) => {
    // 네이버 앱 > 메인화면 검색창 > 상세 주소를 검색해서 이동할 경우 goBack() 혹은 go(-1)가 동작하지 않습니다.
    if (!steps) {
        history.back()
    } else {
        history.go(steps ? -Math.abs(steps) : -1)
    }
}

/**
 * 전달 url에서 쿼리 스트링 제외값 리턴
 * 내부에서만 사용하는 util. test를 위해 export
 * @param url
 */
export const getUrlWithoutQuery = (url: string) => {
    try {
        const result = new URL(url)
        result.search = ''
        return result.toString()
    } catch {
        return url
    }
}

/**
 * history.back 시도시 동작하지 않는 경우, 0.3초 대기 후 callback 호출
 * 내부에서만 사용하는 util. test를 위해 export
 * @param waitTime
 * @param callback
 */
export const goOptionalBack = (waitTime: number, callback: () => void) => {
    let hasNotHistory = true
    const handleHistory = () => {
        hasNotHistory = false
    }

    window.addEventListener(isSafari ? 'pagehide' : 'beforeunload', handleHistory)

    goBack()

    // waitTime 후에도 page unload가 일어나지 않는다면 popup 새창열기로 열려 뒤로가기 동작하지 않는 환경인 것으로 간주하고 페이지 이동시킨다
    setTimeout(function () {
        if (hasNotHistory) {
            callback()
        }
        window.removeEventListener(isSafari ? 'pagehide' : 'beforeunload', handleHistory)
    }, waitTime)
}

export const makeChangeBrowserLocation =
    ({changeAction}: {changeAction: (args: {url: string; type?: 'PUSH' | 'REPLACE'}) => void}) =>
    ({
        url,
        type,
        preserveCircularHistory = false,
    }: {
        url: string
        type?: 'PUSH' | 'REPLACE'
        preserveCircularHistory?: boolean
    }) => {
        const sameTargetUrlAsReferrer =
            window.document.referrer && getUrlWithoutQuery(window.document.referrer) === getUrlWithoutQuery(url)

        if (sameTargetUrlAsReferrer && preserveCircularHistory) {
            goOptionalBack(300, () => changeAction({url, type}))
            return
        }

        changeAction({
            url,
            type,
        })
    }

const tryClose = (url?: string) => {
    if (url === NAVER_CAFE_CLOSE) {
        setTimeout(() => {
            window.self.close()
        }, 100)
    }
}

/**
 * window.location wrapping util.
 * app 별로 화면이동에 요구되는 스펙이 있다면 동일 형태로 적용가능하도록 이 유틸에 optional로 구현.
 * @param url
 * @param type
 * @param preserveCircularHistory
 */
export const changeBrowserLocation = makeChangeBrowserLocation({
    changeAction: ({url, type}) => {
        if (type === 'REPLACE') {
            window.location.replace(url)
            return
        }

        window.location.href = url

        tryClose(url)
    },
})

export const openNewTab = ({url}: {url: string}) => {
    window.open(url, '_blank')
}

/**
 * locatio reload.
 * app별로 새로고침시 요구되는 스펙이 있을 경우 아래 함수에 option추가하여 구현
 */
export const reloadBrowserLocation = () => {
    window.location.reload()
}

/**
 * history.back 동작하지 않을 경우 지정된 url로 이동
 * @param fallbackUrl
 */
export const goBackOrFallbackPage = (fallbackUrl: string, steps?: number) => {
    goBack({history: window.history, steps})

    // goBack()이 동작하지 않았다면 창을 닫거나, referrer로 이동하거나, fallbackUrl로 이동합니다.
    setTimeout(async () => {
        if (isPopupWindow(window)) {
            window.self.close()
            return
        }

        const backUrl = document.referrer ? document.referrer : fallbackUrl
        window.location.href = backUrl

        // window.opener가 있어도 close가 동작하지 않는 경우가 있어, 최종으로 한번 더 fallbackUrl로 이동
        await delayTimeout(500)

        window.location.href = fallbackUrl
        // 평균 ms 단위로 history change가 되므로 500ms delay
    }, 500)
}

export const goBackAndClose = (steps?: number) => {
    goBack({steps})

    // goBack()이 동작하지 않았다면 창을 닫습니다.
    setTimeout(() => {
        window.self.close()
    }, 500)
}

export const browserAlert = ({target = window, message}: {target?: Window; message: string}) => {
    target.alert(message)
}

export const delayedBrowserAlert = ({target = window, message}: {target?: Window; message: string}) => {
    target.requestAnimationFrame(function () {
        // IE에서 alert 호출시 alert으로 블록되었는데 repaint 호출되는 경우가 있어 다음 repaint때 alert 뜨도록 조정
        target.alert(message)
    })
}
